import { supabase } from '@/lib/supabase/cliente'
import { cuantasEnCola } from '@/lib/campo/colaDeFotos'
import { sincronizarFotos } from '@/modules/campo/useColaDeFotos'
import { subirEvidencia } from '@/modules/campo/useEvidencia'

/**
 * LO QUE EL TÉCNICO HIZO EN LA OBRA Y ESPERA SEÑAL.
 *
 * «Llegué» y «terminé» tienen que poder marcarse sin internet: en muchas
 * obras no hay, y hasta ahora el botón simplemente fallaba. Pero un marcaje
 * no es como una foto —que se sube tarde y da igual—: SU VALOR ESTÁ EN LA
 * HORA. Si la llegada se registra cuando vuelve la cobertura, el sistema
 * anota que el técnico llegó dos horas tarde y las horas de la obra, que son
 * la base del cumplimiento y de lo que se le paga, dejan de valer.
 *
 * Por eso aquí se guarda la hora de verdad —la del teléfono en el momento del
 * gesto— y viaja con la acción. La base decide si se la cree
 * (`fn_hora_del_marcaje`): nada del futuro, nada de hace más de tres días, y
 * siempre queda escrito que la puso el teléfono.
 *
 * Misma idea que la cola de fotos y, a propósito, la misma forma: IndexedDB,
 * sin librería, y un intento de envío cuando vuelve la red.
 */

const BASE = 'mega-acciones'
const ALMACEN = 'pendientes'
const VERSION = 1

/** Lo que se puede dejar pendiente. Cada una es una llamada a la base. */
/**
 * Lo que un técnico puede hacer en la obra y guardar si no hay señal.
 *
 * Las dos últimas no son marcajes, son PETICIONES, y están aquí por lo
 * mismo: se piden desde la obra, que es donde no hay cobertura. La de
 * autorización es además la salida que se le ofrece cuando la geocerca lo
 * frena —«si de verdad estás aquí y el GPS falla, pide autorización»—, y una
 * salida que solo funciona con señal no es una salida.
 */
export type ClaseDeAccion =
  | 'llegada'
  | 'salida'
  | 'avance'
  | 'autorizacion'
  | 'mas-tiempo'
  | 'almuerzo'
  | 'recogida'
  | 'traslado'
  | 'devolucion'
  | 'cancelar-recogida'
  | 'visita'

/**
 * LO DE LA HERRAMIENTA, TAMBIÉN SIN SEÑAL (Johan: «que todo pueda
 * gestionarse offline»).
 *
 * Pedir que la recojan, pedir un traslado y devolverla escaneando el QR no
 * cuelgan de una orden ni les importa la hora del gesto: la base no las
 * recibe. Por eso viajan solo con sus argumentos, y en el campo de la orden
 * va su llave —el préstamo, o el código del QR—, que es lo que evita
 * encolar dos veces la misma petición.
 */
export const DE_HERRAMIENTA: readonly ClaseDeAccion[] = [
  'recogida',
  'traslado',
  'devolucion',
  'cancelar-recogida',
]

/**
 * LO QUE VIAJA SOLO CON SUS ARGUMENTOS: lo de herramienta y el registro de
 * la visita de certificación (0256). La visita cuelga de una orden de
 * SERVICIO, no de trabajo, y tampoco le importa la hora del gesto: en el
 * campo de la orden va el id de la orden de servicio, que es su llave.
 */
export const SIN_ORDEN_NI_HORA: readonly ClaseDeAccion[] = [...DE_HERRAMIENTA, 'visita']

export type AccionEnCola = {
  id: string
  clase: ClaseDeAccion
  /** La orden; en las de herramienta, el préstamo o el código del QR. */
  ordenTrabajoId: string
  /** Los argumentos de la función, tal cual se mandarán. */
  argumentos: Record<string, unknown>
  /**
   * LAS FOTOS, ENTERAS, CUANDO TODAVÍA NO HAN PODIDO SUBIR.
   *
   * Terminar exige cinco fotos y reportar un avance, tres. Sin señal no se
   * pueden subir al almacén, y la función de la base quiere RUTAS, no
   * imágenes. Así que las imágenes viajan aquí dentro y al recuperar la
   * conexión se suben primero; solo entonces se llama a la base, ya con sus
   * rutas.
   *
   * Pesan, sí. Pero son el trabajo de la cuadrilla: perderlas por falta de
   * cobertura obligaría a volver a la obra.
   */
  fotos?: Blob[]
  /**
   * Las fotos del material que sobró (0312). Van aparte de la evidencia del
   * trabajo porque no son del acta: se suben igual, primero, y viajan en
   * p_sobrantes_fotos.
   */
  fotosSobrantes?: Blob[]
  /** La hora del gesto, no la del envío. */
  ocurridoEn: string
  creado: number
  /** Cuántas veces se ha intentado, para no insistir eternamente en silencio. */
  intentos: number
  ultimoError: string | null
  /**
   * La base la rechazó por una razón que no cambia reintentando —fuera de la
   * geocerca, la orden ya no admite esa marca—. Se conserva para poder
   * decirlo, no para seguir intentándolo en bucle.
   */
  rechazada?: boolean
  /**
   * El servicio, solo para el cierre: es el contexto que la IA necesita para
   * redactar el acta. No viaja a la base —no es un argumento de la función—,
   * se guarda aquí porque cuando haya señal ya no habrá quien lo recuerde.
   */
  servicio?: string | null
  /**
   * Quién la hizo. La cola vive en el teléfono, no en la sesión: si otra
   * persona entraba en el mismo teléfono, lo pendiente salía con SU sesión y
   * la llegada de uno quedaba a nombre del otro. Solo se envía con la sesión
   * de quien la hizo (auditoría sin conexión, oct 2026).
   */
  usuarioId?: string | null
}

/** El usuario de la sesión guardada, aunque su token haya vencido sin señal. */
function usuarioDelTelefono(): string | null {
  try {
    const crudo = localStorage.getItem('mega-ordenes-sesion')
    return crudo ? (JSON.parse(crudo) as { user?: { id?: string } }).user?.id ?? null : null
  } catch {
    return null
  }
}

let promesaBase: Promise<IDBDatabase> | null = null

/**
 * NINGUNA ESPERA SIN PLAZO.
 *
 * Abrir IndexedDB puede no terminar nunca: si otra pestaña tiene la base
 * abierta con otra versión, el navegador dispara `blocked` y se queda ahí
 * callado. Sin plazo, eso deja el botón «Llegué» en «Tomando ubicación…»
 * para siempre, que es peor que un error: el técnico se queda mirando el
 * teléfono sin saber si marcó o no.
 *
 * Con plazo, si algo va mal falla rápido y la pantalla puede decirlo.
 */
function conPlazo<T>(promesa: Promise<T>, ms: number, queEspera: string): Promise<T> {
  return Promise.race([
    promesa,
    new Promise<T>((_, rechazar) =>
      setTimeout(() => rechazar(new Error(`${queEspera} no respondió a tiempo.`)), ms),
    ),
  ])
}

function abrir(): Promise<IDBDatabase> {
  if (promesaBase) return promesaBase

  promesaBase = conPlazo(
    new Promise<IDBDatabase>((resolver, rechazar) => {
      const solicitud = indexedDB.open(BASE, VERSION)

      solicitud.onupgradeneeded = () => {
        const db = solicitud.result
        if (!db.objectStoreNames.contains(ALMACEN)) {
          const almacen = db.createObjectStore(ALMACEN, { keyPath: 'id' })
          // Se envían en el orden en que ocurrieron: una salida no puede
          // adelantarse a su llegada.
          almacen.createIndex('creado', 'creado', { unique: false })
        }
      }

      solicitud.onsuccess = () => resolver(solicitud.result)
      solicitud.onerror = () => rechazar(solicitud.error)
      // Sin esto el bloqueo es mudo y la promesa queda viva para siempre.
      solicitud.onblocked = () =>
        rechazar(new Error('El almacenamiento del teléfono está ocupado por otra pestaña.'))
    }),
    5000,
    'El almacenamiento del teléfono',
  )

  // Si falló, que el siguiente intento vuelva a probar en vez de heredar el
  // fallo para toda la sesión.
  promesaBase.catch(() => {
    promesaBase = null
  })

  return promesaBase
}

/**
 * ¿Esta es la misma que el técnico acaba de guardar? Entonces no se encola
 * otra.
 *
 * Sin señal la tarjeta tarda en cambiar y quien no lo sepa pulsa otra vez; dos
 * llegadas iguales son dos horas de inicio distintas. Pero la regla era «si
 * hay CUALQUIER llegada de esa orden esperando, no se guarda otra», y eso se
 * comía la vuelta del almuerzo: «Volver del almuerzo» ES una llegada, así que
 * llegada 7:00 → almuerzo 12:00 → vuelta 13:00 sin señal dejaba la vuelta
 * fuera y el servidor nunca sabía que había vuelto. Igual con el avance del
 * segundo día de una obra larga: se descartaba con sus fotos.
 *
 * Ahora solo cuenta como repetida si es LA ÚLTIMA que hay para esa orden, es
 * de la misma clase y de hace menos de seis horas. Llegada, almuerzo,
 * llegada: la tercera entra. Llegada, llegada a los dos minutos: no.
 */
const VENTANA_DE_REPETIDA_MS = 6 * 60 * 60 * 1000

export async function yaHayPendiente(
  clase: ClaseDeAccion,
  ordenTrabajoId: string,
): Promise<AccionEnCola | null> {
  const deEsta = (await accionesPendientes()).filter(
    (a) => a.ordenTrabajoId === ordenTrabajoId && !a.rechazada,
  )
  const ultima = deEsta[deEsta.length - 1]
  if (!ultima || ultima.clase !== clase) return null
  return Date.now() - ultima.creado < VENTANA_DE_REPETIDA_MS ? ultima : null
}

export async function encolarAccion(
  clase: ClaseDeAccion,
  ordenTrabajoId: string,
  argumentos: Record<string, unknown>,
  fotos?: Blob[],
  servicio?: string | null,
  fotosSobrantes?: Blob[],
): Promise<AccionEnCola> {
  const repetida = await yaHayPendiente(clase, ordenTrabajoId)
  if (repetida) return repetida

  const accion: AccionEnCola = {
    id: crypto.randomUUID(),
    clase,
    ordenTrabajoId,
    argumentos,
    ocurridoEn: new Date().toISOString(),
    creado: Date.now(),
    intentos: 0,
    ultimoError: null,
    fotos: fotos && fotos.length > 0 ? fotos : undefined,
    fotosSobrantes: fotosSobrantes && fotosSobrantes.length > 0 ? fotosSobrantes : undefined,
    servicio: servicio ?? undefined,
    usuarioId: usuarioDelTelefono(),
  }

  const db = await abrir()
  await new Promise<void>((resolver, rechazar) => {
    const t = db.transaction(ALMACEN, 'readwrite')
    t.objectStore(ALMACEN).put(accion)
    t.oncomplete = () => resolver()
    t.onerror = () => rechazar(t.error)
  })

  // Que la pantalla se entere: el aviso de «guardado en el teléfono» tiene
  // que salir en el momento, no en la siguiente visita a la pantalla.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mega:cola-de-acciones'))
  }

  return accion
}

export async function accionesPendientes(): Promise<AccionEnCola[]> {
  const db = await abrir()

  return new Promise((resolver) => {
    const t = db.transaction(ALMACEN, 'readonly')
    const s = t.objectStore(ALMACEN).getAll()
    s.onsuccess = () =>
      resolver(((s.result ?? []) as AccionEnCola[]).sort((a, b) => a.creado - b.creado))
    s.onerror = () => resolver([])
  })
}

async function guardar(accion: AccionEnCola): Promise<void> {
  const db = await abrir()
  await new Promise<void>((resolver) => {
    const t = db.transaction(ALMACEN, 'readwrite')
    t.objectStore(ALMACEN).put(accion)
    t.oncomplete = () => resolver()
    t.onerror = () => resolver()
  })
}

async function quitar(id: string): Promise<void> {
  const db = await abrir()
  await new Promise<void>((resolver) => {
    const t = db.transaction(ALMACEN, 'readwrite')
    t.objectStore(ALMACEN).delete(id)
    t.oncomplete = () => resolver()
    t.onerror = () => resolver()
  })
}

/** Qué función de la base atiende cada clase. */
const FUNCION: Record<ClaseDeAccion, string> = {
  llegada: 'fn_marcar_llegada',
  salida: 'fn_marcar_salida',
  avance: 'fn_reportar_avance',
  autorizacion: 'fn_pedir_inicio_sin_ubicacion',
  'mas-tiempo': 'fn_solicitar_mas_tiempo',
  // El almuerzo también se marca sin señal: es en la calle, a la hora que
  // sea, donde más falta la cobertura. Viaja con la hora del gesto (0235).
  almuerzo: 'fn_salir_a_almuerzo',
  recogida: 'fn_pedir_recogida_herramienta',
  traslado: 'fn_solicitar_traslado_herramienta',
  devolucion: 'fn_devolver_por_qr',
  'cancelar-recogida': 'fn_cancelar_recogida_herramienta',
  // La visita de certificación, en la obra sin señal (0256).
  visita: 'fn_registrar_visita',
}

/** Cómo se llama cada una cuando hay que contárselo al técnico. */
export const NOMBRE_DE_ACCION: Record<ClaseDeAccion, string> = {
  llegada: 'la llegada',
  salida: 'el cierre',
  avance: 'el avance',
  autorizacion: 'el permiso para empezar',
  'mas-tiempo': 'la petición de más tiempo',
  almuerzo: 'la salida a almorzar',
  recogida: 'la petición de recogida',
  traslado: 'la petición de traslado',
  devolucion: 'la devolución',
  'cancelar-recogida': 'la cancelación de la recogida',
  visita: 'el registro de la visita',
}

/**
 * ¿Falló por falta de señal? Sin red, o la petición ni siquiera llegó (el
 * cliente de Supabase no lanza: devuelve un error sin código con el mensaje
 * del `fetch`). Lo usan los ganchos para encolar en vez de mostrar un error.
 */
export function esFaltaDeSenal(error: { code?: string; message?: string } | null): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true
  if (!error) return false
  return !error.code && /fetch|network|timeout|load failed/i.test(error.message ?? '')
}

/**
 * Quitar de la lista una que la base rechazó, cuando el técnico ya la leyó.
 * Antes se quedaban para siempre en la pantalla.
 */
export async function descartarAccion(id: string): Promise<void> {
  await quitar(id)
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('mega:cola-de-acciones'))
  }
}

/**
 * Intenta mandar lo pendiente, en orden.
 *
 * Se para al primer fallo de red: si no hay señal para la primera, no la hay
 * para las siguientes, y seguir solo gasta batería. Un rechazo de la base
 * —la orden ya no está en ese estado, por ejemplo— no es falta de señal: esa
 * acción se marca con su motivo y se deja para que alguien la mire, en vez de
 * reintentarla para siempre.
 */
export async function enviarPendientes(): Promise<{ enviadas: number; quedan: number }> {
  const quien = usuarioDelTelefono()
  // Las de otro usuario esperan a que él vuelva a entrar en este teléfono.
  const pendientes = (await accionesPendientes()).filter(
    (a) => !a.usuarioId || !quien || a.usuarioId === quien,
  )
  let enviadas = 0

  for (let accion of pendientes) {
    // Las rechazadas no se reintentan: esperan a que una persona haga algo.
    if (accion.rechazada) continue

    try {
      /*
       * PRIMERO LAS FOTOS, DESPUÉS EL MARCAJE.
       *
       * Si se llamara a la base antes, la salida quedaría registrada sin
       * evidencia y la orden pasaría a precierre sin una sola foto —que es
       * justo lo que la 0205 vino a prohibir—. Si una foto no sube, se corta
       * y se reintenta entero en la siguiente vuelta: nada a medias.
       */
      if (accion.fotos && accion.fotos.length > 0) {
        const rutas: string[] = []
        for (const foto of accion.fotos) {
          rutas.push(
            await subirEvidencia(
              accion.ordenTrabajoId,
              new File([foto], 'evidencia.jpg', { type: foto.type || 'image/jpeg' }),
            ),
          )
        }

        // Ya están arriba: se quitan de la cola para no volver a subirlas si
        // el marcaje falla por otra cosa.
        accion = {
          ...accion,
          fotos: undefined,
          argumentos: { ...accion.argumentos, p_fotos: rutas },
        }
        await guardar(accion)
      }

      // Las del material que sobró (0312), por el mismo camino.
      if (accion.fotosSobrantes && accion.fotosSobrantes.length > 0) {
        const rutas: string[] = []
        for (const foto of accion.fotosSobrantes) {
          rutas.push(
            await subirEvidencia(
              accion.ordenTrabajoId,
              new File([foto], 'sobrante.jpg', { type: foto.type || 'image/jpeg' }),
            ),
          )
        }
        accion = {
          ...accion,
          fotosSobrantes: undefined,
          argumentos: { ...accion.argumentos, p_sobrantes_fotos: rutas },
        }
        await guardar(accion)
      }

      /*
       * EL ACTA SE MEJORA AHORA, QUE ES CUANDO HAY SEÑAL.
       *
       * Al cerrar en la obra se intenta pulir con la IA lo que escribió el
       * técnico, pero sin cobertura esa llamada no sale y el cierre se
       * guardaba con el resumen en nulo. Al subirlo, ya con señal, no se
       * volvía a intentar nunca: el acta del cliente se quedaba para siempre
       * con el texto crudo escrito a dedo en un teléfono, bajo el sol.
       *
       * Aquí hay señal —si no, no estaríamos en este punto—, así que se pide
       * la redacción antes de registrar la salida. Si falla, se sigue igual:
       * la base cae al texto del técnico y el acta nunca queda vacía. Mejorar
       * la redacción no puede costar un cierre.
       */
      if (
        accion.clase === 'salida' &&
        !accion.argumentos.p_resumen_acta &&
        typeof accion.argumentos.p_comentario === 'string' &&
        accion.argumentos.p_comentario.trim().length > 0
      ) {
        try {
          const { data: mejora } = await supabase.functions.invoke('mejorar-acta', {
            body: {
              texto: accion.argumentos.p_comentario,
              servicio: accion.servicio ?? '',
            },
          })
          const redaccion = (mejora as { redaccion?: string | null } | null)?.redaccion?.trim()

          if (redaccion) {
            accion = {
              ...accion,
              argumentos: { ...accion.argumentos, p_resumen_acta: redaccion },
            }
            // Se guarda ya: si el registro falla por otra cosa, la redacción
            // no se vuelve a pedir ni se pierde.
            await guardar(accion)
          }
        } catch {
          /* sin IA se sigue: el acta sale con el texto del técnico */
        }
      }

      /*
       * LA VISITA, DESPUÉS DE SUS FOTOS (0256). La base no la registra sin
       * una foto de la visita, y esas fotos esperan en su propia cola. Si
       * alguna de esta orden sigue en el teléfono, se intenta subirla aquí;
       * si no sube, la visita espera a la próxima vuelta en vez de llegar
       * antes que sus fotos y quedar rechazada por «falta la foto».
       */
      if (accion.clase === 'visita' && (await cuantasEnCola(accion.ordenTrabajoId)) > 0) {
        await sincronizarFotos()
        if ((await cuantasEnCola(accion.ordenTrabajoId)) > 0) continue
      }

      const deHerramienta = SIN_ORDEN_NI_HORA.includes(accion.clase)
      const { data, error } = await supabase.rpc(
        FUNCION[accion.clase] as 'fn_marcar_llegada',
        (deHerramienta
          ? accion.argumentos
          : {
              ...accion.argumentos,
              p_orden_trabajo_id: accion.ordenTrabajoId,
              p_ocurrido_en: accion.ocurridoEn,
            }) as never,
      )

      /*
       * LA DEVOLUCIÓN PUEDE CONTESTAR «CONFIRMAR» SIN SER ERROR: la unidad
       * escaneada no es la que estaba apartada. Con señal se le pregunta al
       * técnico en el momento; sin señal ya no hay a quién, y aceptarla sola
       * movería el inventario sin que nadie lo decidiera. Se deja a la vista
       * con la pregunta, para que la conteste escaneando otra vez.
       */
      const respuesta = Array.isArray(data)
        ? (data[0] as { estado?: string; mensaje?: string })
        : null
      if (!error && accion.clase === 'devolucion' && respuesta && respuesta.estado !== 'devuelta') {
        await guardar({
          ...accion,
          intentos: accion.intentos + 1,
          ultimoError: `${
            respuesta.mensaje ?? 'Hay que confirmarla.'
          } Escanéala otra vez con señal para confirmarla.`,
          rechazada: true,
        })
        continue
      }

      if (error) {
        /*
         * GEO01 NO ES UN AVISO: ES UN RECHAZO. Y creerlo un aviso costaba la
         * marca entera.
         *
         * Lo había puesto como «la llegada quedó registrada igual» y lo
         * borraba de la cola. Es falso: la base LANZA la excepción y no
         * escribe nada. La prueba del ciclo completo lo destapó —«estás a
         * 1338 m y la geocerca es de 90 m»—: la cola se vaciaba, la pantalla
         * no decía nada y la llegada desaparecía sin dejar rastro. El peor
         * fallo posible en esto, porque es silencioso.
         *
         * Reintentarlo tampoco sirve: si estaba lejos, lo seguirá estando.
         * Se marca como rechazada, se conserva con su motivo y la pantalla
         * lo dice, que es lo único honesto: el técnico tiene que saber que
         * esa marca no entró y pedir autorización.
         */
        // FEC01 es el día equivocado: reintentarlo mañana tampoco lo
        // arreglaría, y mañana sería todavía menos su día.
        //
        // Las de herramienta no tienen geocerca ni día: si la base contesta
        // con un código —ya la pidieron, ya la devolvieron, no es tuya—, es
        // una respuesta, no falta de señal. Sin código, o de conexión (08…)
        // o de la API (PGRST…, el token vencido), se reintenta.
        //
        // OJO CON EL CÓDIGO: la base lanza `errcode = 'check_violation'`, pero
        // lo que llega aquí es el SQLSTATE, «23514». Se comparaba con el
        // nombre y nunca coincidía: «esa orden ya no admite marcas» se
        // reintentaba en cada vuelta para siempre, sin decírselo al técnico.
        // Lo destapó la auditoría sin conexión (oct 2026). P0002 es «no
        // existe» y FEC02 otra fecha inválida: tampoco cambian reintentando.
        //
        // 42501 (sin permiso) NO entra: es lo que contesta la base cuando la
        // sesión se perdió sin señal, y eso se arregla entrando otra vez.
        const definitivo =
          ['GEO01', 'FEC01', 'FEC02', '23514', 'P0002', 'check_violation'].includes(error.code) ||
          (deHerramienta && Boolean(error.code) && !/^(08|53|57|42501|PGRST)/.test(error.code))

        await guardar({
          ...accion,
          intentos: accion.intentos + 1,
          ultimoError: error.message,
          rechazada: definitivo,
        })
        // SIN SEÑAL SE PARA AQUÍ. El cliente de Supabase no lanza cuando la
        // red falla: devuelve un error sin código. Se seguía con la
        // siguiente, y con la cobertura yendo y viniendo una salida podía
        // llegar antes que su llegada. La tanda se corta y se reintenta
        // entera, en orden, cuando vuelva.
        if (esFaltaDeSenal(error)) break
        continue
      }

      await quitar(accion.id)
      enviadas++
    } catch {
      // Sin red. Se deja todo como está y se reintenta cuando vuelva.
      break
    }
  }

  return { enviadas, quedan: (await accionesPendientes()).length }
}
